// Probability Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const probabilitySamples = {
  fundamental: [
    {
      id: 'prob_fund_01',
      problem: "Find the probability of getting exactly 2 heads when flipping a fair coin 3 times",
      problemType: "probability",
      difficulty: "fundamental",
      description: "Basic binomial probability with coin flips",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: 0, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 3",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 2",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.5",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 2 heads",
            value: "P(X=2) = 3/8 = 0.375",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "binomial_probability_formula",
            name: "Binomial Probability Formula",
            type: "formulas",
            description: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=3, k=2, p=0.5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(3,2) = 3",
            value: "Step 2: C(3,2) = 3!/(2!1!) = 3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=2) = C(3,2) × (0.5)² × (0.5)¹",
            value: "Step 3: P(X=2) = 3 × 0.25 × 0.5",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=2) = 3 × 0.125 = 0.375",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_fund_02',
      problem: "Find the probability of getting exactly 1 head when flipping a fair coin 2 times",
      problemType: "probability",
      difficulty: "fundamental",
      description: "Simple binomial with 2 trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: 0, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 2",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 1",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.5",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 1 head",
            value: "P(X=1) = 1/2 = 0.5",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=2, k=1, p=0.5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(2,1) = 2",
            value: "Step 2: C(2,1) = 2!/(1!1!) = 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=1) = C(2,1) × (0.5)¹ × (0.5)¹",
            value: "Step 3: P(X=1) = 2 × 0.5 × 0.5",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=1) = 2 × 0.25 = 0.5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_fund_03',
      problem: "Find the probability of getting exactly 3 heads when flipping a fair coin 4 times",
      problemType: "probability",
      difficulty: "fundamental",
      description: "Binomial with 4 trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 4",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 3",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.5",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 3 heads",
            value: "P(X=3) = 1/4 = 0.25",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=4, k=3, p=0.5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(4,3) = 4",
            value: "Step 2: C(4,3) = 4!/(3!1!) = 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=3) = C(4,3) × (0.5)³ × (0.5)¹",
            value: "Step 3: P(X=3) = 4 × 0.125 × 0.5",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=3) = 4 × 0.0625 = 0.25",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_fund_04',
      problem: "Find the probability of getting exactly 0 heads when flipping a fair coin 2 times",
      problemType: "probability",
      difficulty: "fundamental",
      description: "Binomial with 0 successes",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 2",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 0",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.5",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 0 heads",
            value: "P(X=0) = 1/4 = 0.25",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=2, k=0, p=0.5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(2,0) = 1",
            value: "Step 2: C(2,0) = 2!/(0!2!) = 1",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=0) = C(2,0) × (0.5)⁰ × (0.5)²",
            value: "Step 3: P(X=0) = 1 × 1 × 0.25",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=0) = 1 × 0.25 = 0.25",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_fund_05',
      problem: "Find the probability of getting exactly 4 heads when flipping a fair coin 4 times",
      problemType: "probability",
      difficulty: "fundamental",
      description: "Binomial with all successes",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 4",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 4",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.5",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 4 heads",
            value: "P(X=4) = 1/16 = 0.0625",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=4, k=4, p=0.5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(4,4) = 1",
            value: "Step 2: C(4,4) = 4!/(4!0!) = 1",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=4) = C(4,4) × (0.5)⁴ × (0.5)⁰",
            value: "Step 3: P(X=4) = 1 × 0.0625 × 1",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=4) = 1 × 0.0625 = 0.0625",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'prob_inter_01',
      problem: "Find the probability of getting exactly 3 heads when flipping a biased coin (p=0.6) 5 times",
      problemType: "probability",
      difficulty: "intermediate",
      description: "Binomial with biased coin",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 5",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 3",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.6",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 3 heads",
            value: "P(X=3) = 0.3456",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=5, k=3, p=0.6",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(5,3) = 10",
            value: "Step 2: C(5,3) = 5!/(3!2!) = 10",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=3) = C(5,3) × (0.6)³ × (0.4)²",
            value: "Step 3: P(X=3) = 10 × 0.216 × 0.16",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=3) = 10 × 0.03456 = 0.3456",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_inter_02',
      problem: "Find the probability of getting exactly 2 sixes when rolling a fair die 4 times",
      problemType: "probability",
      difficulty: "intermediate",
      description: "Binomial with dice rolls",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of die rolls",
            value: "n = 4",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of sixes",
            value: "k = 2",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of six on one roll",
            value: "p = 1/6",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 2 sixes",
            value: "P(X=2) = 0.1157",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=4, k=2, p=1/6",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(4,2) = 6",
            value: "Step 2: C(4,2) = 4!/(2!2!) = 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=2) = C(4,2) × (1/6)² × (5/6)²",
            value: "Step 3: P(X=2) = 6 × (1/36) × (25/36)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=2) = 6 × 0.0278 × 0.6944 = 0.1157",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_inter_03',
      problem: "Find the probability of getting exactly 1 success in 3 trials with success probability 0.3",
      problemType: "probability",
      difficulty: "intermediate",
      description: "General binomial probability",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 3",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 1",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.3",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 1 success",
            value: "P(X=1) = 0.441",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=3, k=1, p=0.3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(3,1) = 3",
            value: "Step 2: C(3,1) = 3!/(1!2!) = 3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=1) = C(3,1) × (0.3)¹ × (0.7)²",
            value: "Step 3: P(X=1) = 3 × 0.3 × 0.49",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=1) = 3 × 0.147 = 0.441",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_inter_04',
      problem: "Find the probability of getting exactly 2 successes in 5 trials with success probability 0.4",
      problemType: "probability",
      difficulty: "intermediate",
      description: "Binomial with larger number of trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 5",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 2",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.4",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 2 successes",
            value: "P(X=2) = 0.3456",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=5, k=2, p=0.4",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(5,2) = 10",
            value: "Step 2: C(5,2) = 5!/(2!3!) = 10",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=2) = C(5,2) × (0.4)² × (0.6)³",
            value: "Step 3: P(X=2) = 10 × 0.16 × 0.216",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=2) = 10 × 0.03456 = 0.3456",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_inter_05',
      problem: "Find the probability of getting exactly 0 successes in 3 trials with success probability 0.2",
      problemType: "probability",
      difficulty: "intermediate",
      description: "Binomial with 0 successes",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 3",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 0",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.2",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 0 successes",
            value: "P(X=0) = 0.512",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=3, k=0, p=0.2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(3,0) = 1",
            value: "Step 2: C(3,0) = 3!/(0!3!) = 1",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=0) = C(3,0) × (0.2)⁰ × (0.8)³",
            value: "Step 3: P(X=0) = 1 × 1 × 0.512",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=0) = 1 × 0.512 = 0.512",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'prob_hard_01',
      problem: "Find the probability of getting exactly 4 heads when flipping a biased coin (p=0.7) 6 times",
      problemType: "probability",
      difficulty: "harder",
      description: "Binomial with biased coin and larger trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of coin flips",
            value: "n = 6",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of heads",
            value: "k = 4",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of heads on one flip",
            value: "p = 0.7",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 4 heads",
            value: "P(X=4) = 0.3241",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=6, k=4, p=0.7",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(6,4) = 15",
            value: "Step 2: C(6,4) = 6!/(4!2!) = 15",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=4) = C(6,4) × (0.7)⁴ × (0.3)²",
            value: "Step 3: P(X=4) = 15 × 0.2401 × 0.09",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=4) = 15 × 0.021609 = 0.3241",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_hard_02',
      problem: "Find the probability of getting exactly 3 sixes when rolling a fair die 8 times",
      problemType: "probability",
      difficulty: "harder",
      description: "Binomial with dice and larger trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of die rolls",
            value: "n = 8",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of sixes",
            value: "k = 3",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of six on one roll",
            value: "p = 1/6",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 3 sixes",
            value: "P(X=3) = 0.1042",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=8, k=3, p=1/6",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(8,3) = 56",
            value: "Step 2: C(8,3) = 8!/(3!5!) = 56",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=3) = C(8,3) × (1/6)³ × (5/6)⁵",
            value: "Step 3: P(X=3) = 56 × (1/216) × (3125/7776)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=3) = 56 × 0.00463 × 0.4019 = 0.1042",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_hard_03',
      problem: "Find the probability of getting exactly 5 successes in 10 trials with success probability 0.3",
      problemType: "probability",
      difficulty: "harder",
      description: "Binomial with larger number of trials",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 10",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 5",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.3",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 5 successes",
            value: "P(X=5) = 0.1029",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=10, k=5, p=0.3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(10,5) = 252",
            value: "Step 2: C(10,5) = 10!/(5!5!) = 252",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=5) = C(10,5) × (0.3)⁵ × (0.7)⁵",
            value: "Step 3: P(X=5) = 252 × 0.00243 × 0.16807",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=5) = 252 × 0.000408 = 0.1029",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_hard_04',
      problem: "Find the probability of getting exactly 2 successes in 7 trials with success probability 0.25",
      problemType: "probability",
      difficulty: "harder",
      description: "Binomial with fractional probability",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 7",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 2",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.25",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 2 successes",
            value: "P(X=2) = 0.3115",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=7, k=2, p=0.25",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(7,2) = 21",
            value: "Step 2: C(7,2) = 7!/(2!5!) = 21",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=2) = C(7,2) × (0.25)² × (0.75)⁵",
            value: "Step 3: P(X=2) = 21 × 0.0625 × 0.2373",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=2) = 21 × 0.01483 = 0.3115",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'prob_hard_05',
      problem: "Find the probability of getting exactly 1 success in 6 trials with success probability 0.15",
      problemType: "probability",
      difficulty: "harder",
      description: "Binomial with low success probability",
      graphData: {
        nodes: [
          {
            id: "probability",
            name: "Probability",
            type: "concepts",
            description: "The likelihood of an event occurring",
            value: "P(event) = favorable/total",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "binomial_distribution",
            name: "Binomial Distribution",
            type: "concepts",
            description: "Probability distribution for repeated independent trials",
            value: "P(X=k) = C(n,k) × p^k × (1-p)^(n-k)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "n_value",
            name: "Number of Trials",
            type: "values",
            description: "Total number of trials",
            value: "n = 6",
            importance: 8,
            x: -120, y: 60, z: 35
          },
          {
            id: "k_value",
            name: "Number of Successes",
            type: "values",
            description: "Desired number of successes",
            value: "k = 1",
            importance: 8,
            x: 120, y: 60, z: 35
          },
          {
            id: "p_value",
            name: "Success Probability",
            type: "values",
            description: "Probability of success on one trial",
            value: "p = 0.15",
            importance: 9,
            x: 0, y: 60, z: 35
          },
          {
            id: "probability_result",
            name: "Final Probability",
            type: "values",
            description: "The probability of getting exactly 1 success",
            value: "P(X=1) = 0.3993",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "formulas",
            description: "Number of ways to choose k items from n items",
            value: "C(n,k) = n!/(k!(n-k)!)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "binomial_probability",
            name: "Binomial Probability",
            type: "formulas",
            description: "Probability of k successes in n trials",
            value: "P(X=k) = C(n,k) × p^k × q^(n-k)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "identify_parameters",
            name: "Identify Parameters",
            type: "steps",
            description: "Extract n, k, and p from the problem",
            value: "Step 1: n=6, k=1, p=0.15",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_combination",
            name: "Calculate Combination",
            type: "steps",
            description: "Find C(6,1) = 6",
            value: "Step 2: C(6,1) = 6!/(1!5!) = 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_formula",
            name: "Apply Binomial Formula",
            type: "steps",
            description: "Use P(X=1) = C(6,1) × (0.15)¹ × (0.85)⁵",
            value: "Step 3: P(X=1) = 6 × 0.15 × 0.4437",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_calculation",
            name: "Final Calculation",
            type: "steps",
            description: "Calculate the final probability",
            value: "Step 4: P(X=1) = 6 × 0.06656 = 0.3993",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "probability", to: "binomial_distribution", type: "includes", strength: 9 },
          { from: "binomial_distribution", to: "combination_formula", type: "uses", strength: 9 },
          { from: "binomial_distribution", to: "binomial_probability", type: "expressed as", strength: 10 },
          { from: "n_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "k_value", to: "binomial_distribution", type: "used in", strength: 8 },
          { from: "p_value", to: "binomial_distribution", type: "used in", strength: 9 },
          { from: "binomial_distribution", to: "probability_result", type: "gives", strength: 10 },
          { from: "probability", to: "identify_parameters", type: "requires", strength: 8 },
          { from: "identify_parameters", to: "calculate_combination", type: "enables", strength: 9 },
          { from: "calculate_combination", to: "apply_binomial_formula", type: "used in", strength: 10 },
          { from: "apply_binomial_formula", to: "final_calculation", type: "leads to", strength: 10 },
          { from: "final_calculation", to: "probability_result", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 